import { useState, useRef, useEffect, useCallback } from "react"
import { Platform, Pressable, View } from "react-native"
import { TrueSheet } from "@lodev09/react-native-true-sheet"
import { createPortal } from "react-dom"
import { ar } from "@src/i18n"
import { MCIcon, Text } from "./core"
import { Alert } from "./ui/confirm"

type MenuActionsProps = {
  /** Display name of the item (used in sheet title and confirmations) */
  itemName: string
  status?: "active" | "inactive"
  /** Called when user confirms archive/activate toggle */
  onToggleStatus?: (newStatus: "active" | "inactive") => unknown
  /** Called when user confirms delete. If returns a string, show as error alert. */
  onDelete: () => string | undefined | Promise<string | undefined>
  /** Called when user taps edit */
  onEdit?: () => unknown
  /** Whether a mutation is in progress */
  isPending?: boolean
  //
  className?: string
}

export function MenuActions({ itemName, status, onToggleStatus, onDelete, onEdit, isPending, className }: MenuActionsProps) {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<View>(null)
  const sheetRef = useRef<TrueSheet>(null)
  const [menuPos, setMenuPos] = useState<{ top: number; left?: number; right?: number } | null>(null)

  const openMenu = useCallback(() => {
    if (Platform.OS !== "web") return
    const el = wrapperRef.current as unknown as HTMLElement | null
    if (!el) return
    const rect = el.getBoundingClientRect()
    const menuWidth = 160 // min-w-40
    const margin = 8
    const top = rect.bottom + 4

    // Prefer anchoring to the trigger's right edge (menu opens leftward).
    // If there isn't enough room to the left of the anchor (menu would extend
    // off the left side of the viewport), anchor to the trigger's left edge
    // instead (menu opens rightward), clamped in-bounds.
    const rightOffset = window.innerWidth - rect.right
    if (rect.right >= menuWidth + margin) {
      setMenuPos({ top, right: rightOffset })
    } else {
      setMenuPos({ top, left: Math.max(margin, Math.min(rect.left, window.innerWidth - menuWidth - margin)) })
    }
    setOpen(true)
  }, [])

  useEffect(() => {
    if (Platform.OS !== "web" || !open) return
    const handleClickOutside = (event: MouseEvent) => {
      const domNode = wrapperRef.current as unknown as HTMLElement
      const menuEl = document.getElementById("menu-actions-portal")
      if (domNode && !domNode.contains(event.target as Node) && menuEl && !menuEl.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    const close = () => setOpen(false)
    document.addEventListener("mousedown", handleClickOutside)
    window.addEventListener("scroll", close, true)
    window.addEventListener("resize", close)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      window.removeEventListener("scroll", close, true)
      window.removeEventListener("resize", close)
    }
  }, [open])

  const handleToggleStatus = () => {
    setOpen(false)
    sheetRef.current?.dismiss()
    const newStatus = status === "active" ? "inactive" : "active"
    const actionLabel = newStatus === "inactive" ? ar.inactive : ar.active

    Alert.alert(ar.confirm, `${ar.areYouSure}`, [
      { text: ar.cancel, style: "cancel" },
      {
        text: actionLabel,
        onPress: () => onToggleStatus?.(newStatus),
      },
    ])
  }

  const handleDelete = async () => {
    setOpen(false)
    sheetRef.current?.dismiss()
    Alert.alert(ar.confirmDelete, `${ar.areYouSure}`, [
      { text: ar.cancel, style: "cancel" },
      {
        text: ar.delete,
        style: "destructive",
        onPress: async () => {
          const result = await onDelete()
          if (typeof result === "string") {
            Alert.alert(ar.error, result)
          }
        },
      },
    ])
  }

  const handleEdit = () => {
    setOpen(false)
    sheetRef.current?.dismiss()
    onEdit?.()
  }

  const isArchived = status === "inactive"
  const toggleLabel = isArchived ? ar.active : ar.inactive
  const toggleIcon = isArchived ? "eye" : "eye-off"
  const toggleColor = isArchived ? "#16A34A" : "#6B7280"

  if (Platform.OS === "web") {
    return (
      <View className={`absolute top-2 end-2 z-20 ${className}`} ref={wrapperRef}>
        <Pressable
          onPress={open ? () => setOpen(false) : openMenu}
          className="p-1.5 bg-white/80 backdrop-blur-sm hover:bg-white rounded-full shadow-sm hover:shadow-md transition-all border border-gray-100/50"
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          disabled={isPending}
        >
          <MCIcon name={isPending ? "loading" : "dots-vertical"} size={18} color="#4B5563" />
        </Pressable>

        {open &&
          menuPos &&
          createPortal(
            <View
              id="menu-actions-portal"
              style={{ position: "fixed", top: menuPos.top, left: menuPos.left, right: menuPos.right, zIndex: 9999 }}
              className="bg-white rounded shadow-lg border border-gray-200 overflow-hidden min-w-40"
            >
              {onEdit && (
                <Pressable onPress={handleEdit} className="h-10 flex-row items-center px-3 hover:bg-blue-50 active:bg-blue-100 transition-colors">
                  <MCIcon name="pencil" size={20} color="#3B82F6" />
                  <Text className="ms-2 text-sm text-gray-700 font-medium">{ar.edit}</Text>
                </Pressable>
              )}
              {status != null && onToggleStatus && (
                <Pressable onPress={handleToggleStatus} className="h-10 flex-row items-center px-3 hover:bg-gray-50 active:bg-blue-50 transition-colors">
                  <MCIcon name={toggleIcon} size={20} color={toggleColor} />
                  <Text className="ms-2 text-sm text-gray-700 font-medium">{toggleLabel}</Text>
                </Pressable>
              )}
              <Pressable onPress={handleDelete} className="h-10 flex-row items-center px-3 hover:bg-red-50 active:bg-red-100 transition-colors border-t border-gray-100">
                <MCIcon name="delete" size={20} color="#DC2626" />
                <Text className="ms-2 text-sm text-red-600 font-medium">{ar.delete}</Text>
              </Pressable>
            </View>,
            document.body,
          )}
      </View>
    )
  }

  return (
    <>
      <Pressable
        onPress={() => sheetRef.current?.present()}
        className={`absolute top-2 end-2 p-1.5 bg-white/80 backdrop-blur-sm rounded-full shadow-sm ${className}`}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        disabled={isPending}
      >
        <MCIcon name={isPending ? "loading" : "dots-vertical"} size={18} color="#6B7280" />
      </Pressable>

      <TrueSheet ref={sheetRef} detents={["auto"]}>
        <View className="flex-row items-center justify-between pb-4 pt-4 px-3 mb-2">
          <Text className="text-xl font-bold text-gray-900">{itemName}</Text>
          <Pressable onPress={() => sheetRef.current?.dismiss()} className="p-2 bg-gray-100 rounded-full">
            <MCIcon name="close" size={20} color="#6B7280" />
          </Pressable>
        </View>

        {onEdit && (
          <Pressable onPress={handleEdit} className="flex-row items-center p-3 bg-blue-50 rounded-md active:bg-blue-100 mx-2 mb-2">
            <MCIcon name="pencil" size={24} color="#3B82F6" className="ml-3" />
            <Text className="font-bold text-blue-600 text-lg mb-0.5 flex-1">{ar.edit}</Text>
            <MCIcon name="chevron-left" size={24} color="#9CA3AF" />
          </Pressable>
        )}

        {status != null && onToggleStatus && (
          <Pressable onPress={handleToggleStatus} className="flex-row items-center p-3 bg-gray-50 rounded-md active:bg-gray-100 mx-2 mb-2">
            <MCIcon name={toggleIcon} size={24} color={toggleColor} className="ml-3" />
            <Text className="font-bold text-gray-900 text-lg mb-0.5 flex-1">{toggleLabel}</Text>
            <MCIcon name="chevron-left" size={24} color="#9CA3AF" />
          </Pressable>
        )}

        <Pressable onPress={handleDelete} className="flex-row items-center p-3 bg-red-50 rounded-md active:bg-red-100 mx-2">
          <MCIcon name="delete" size={24} color="#DC2626" className="ml-3" />
          <Text className="font-bold text-red-600 text-lg mb-0.5 flex-1">{ar.delete}</Text>
        </Pressable>
      </TrueSheet>
    </>
  )
}
